import { useEffect, useState } from "react";
import { CreditCard, Lightbulb, Loader2, Settings2, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

export function AutoTopupSettings({ sim }) {
  const { t, fmt, lang } = useLanguage();
  const [enabled, setEnabled] = useState(!!sim.auto_topup);
  const [open, setOpen] = useState(!!sim.auto_topup);
  const [thresholdType, setThresholdType] = useState(sim.auto_topup_threshold_mb ? "mb" : "pct");
  const [thresholdValue, setThresholdValue] = useState(sim.auto_topup_threshold_mb || sim.auto_topup_threshold_pct || 10);
  const [mode, setMode] = useState(sim.auto_topup_mode || "same");
  const [packageId, setPackageId] = useState(sim.auto_topup_package_id || "");
  const [endDate, setEndDate] = useState(sim.auto_topup_end_date || sim.return_date || "");
  const [topups, setTopups] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [history, setHistory] = useState(null);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/saved-card").then(({ data }) => setCard(data.data)).catch(() => setCard({ saved: false }));
  }, []);

  useEffect(() => {
    if (!open) return;
    api.get(`/esims/${sim.iccid}/auto-topup/suggestion`).then(({ data }) => setSuggestion(data.data)).catch(() => setSuggestion(null));
    api.get(`/esims/${sim.iccid}/auto-topup/history`).then(({ data }) => setHistory(data.data || [])).catch(() => setHistory([]));
  }, [open, sim.iccid]);

  useEffect(() => {
    if (!open || topups !== null) return;
    api.get(`/esims/${sim.iccid}/topups`).then(({ data }) => setTopups(data.data || [])).catch(() => setTopups([]));
  }, [open, topups, sim.iccid]);

  const payload = (value = enabled) => ({
    enabled: value,
    threshold_pct: thresholdType === "pct" ? Number(thresholdValue) : null,
    threshold_mb: thresholdType === "mb" ? Number(thresholdValue) : null,
    mode,
    package_id: mode === "custom" ? packageId : null,
    end_date: endDate || null,
  });

  const save = async (value = enabled) => {
    setBusy(true);
    try {
      await api.post(`/esims/${sim.iccid}/auto-topup`, payload(value));
      setEnabled(value);
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    const next = !enabled;
    if (next) {
      setEnabled(true);
      setOpen(true);
      return;
    }
    setEnabled(false);
    setOpen(false);
    await save(false);
  };

  const applySuggestion = () => {
    if (!suggestion?.threshold_mb) return;
    setThresholdType("mb");
    setThresholdValue(suggestion.threshold_mb);
  };

  const setupCard = async () => {
    setCardBusy(true);
    try {
      const { data } = await api.post("/payments/saved-card/setup", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      toast.error(t("misc.error"));
      setCardBusy(false);
    }
  };

  const historyAmount = (h) => {
    if (h.amount != null) {
      try { return new Intl.NumberFormat(lang, { style: "currency", currency: h.currency || "EUR" }).format(h.amount); }
      catch { return `${h.amount} ${h.currency || "EUR"}`; }
    }
    return h.amount_usd != null ? fmt(h.amount_usd) : "—";
  };

  return (
    <div data-testid={`auto-topup-settings-${sim.iccid}`} className="mt-6 rounded-3xl border border-slate-200/80 bg-[#FAF5EE]/50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label data-testid={`auto-topup-${sim.iccid}`} className="inline-flex items-center gap-3 cursor-pointer select-none">
          <input type="checkbox" checked={enabled} onChange={toggle} className="w-4 h-4 accent-[#0D9488]" />
          <span className="text-sm font-medium text-[var(--text-secondary)]">{t("wallet.autoTopup")}</span>
        </label>
        <button type="button" data-testid={`auto-topup-config-${sim.iccid}`} onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
          <Settings2 className="w-3.5 h-3.5" /> {open ? t("auto.close") : t("auto.configure")}
        </button>
      </div>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {card?.saved ? (
              <span data-testid={`saved-card-status-${sim.iccid}`} className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 font-mono-t text-[9px] font-bold uppercase tracking-widest">
                <CreditCard className="w-3.5 h-3.5" /> {card.brand} ·· {card.last4}
              </span>
            ) : (
              <button type="button" data-testid={`saved-card-setup-${sim.iccid}`} onClick={setupCard} disabled={cardBusy}
                className="inline-flex items-center gap-2 rounded-full bg-[#1E293B] text-white px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest hover:bg-[#FF6B35] transition-colors disabled:opacity-60">
                {cardBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                {t("auto.saveCard")}
              </button>
            )}
            <span className="text-slate-500">{t("auto.fallbackNote")}</span>
          </div>

          {suggestion?.threshold_mb && (
            <div data-testid={`auto-topup-smart-suggestion-${sim.iccid}`} className="rounded-2xl border border-[#0D9488]/20 bg-[#E6F7F5] p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-[#0D9488] shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-bold text-sm text-slate-900">
                    {t("auto.smartTitle").replace("{mb}", suggestion.threshold_mb)}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {t("auto.smartSub")
                      .replace("{avg}", suggestion.daily_avg_mb)
                      .replace("{days}", suggestion.remaining_days)}
                  </p>
                </div>
              </div>
              <button type="button" data-testid={`auto-topup-apply-smart-${sim.iccid}`} onClick={applySuggestion}
                className="rounded-full bg-white border border-[#0D9488]/30 px-4 py-2 font-mono-t text-[9px] font-bold uppercase tracking-widest text-[#0D9488] hover:bg-[#0D9488] hover:text-white transition-colors">
                {t("auto.smartApply")}
              </button>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("auto.threshold")}</span>
              <div className="mt-1 flex rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <input data-testid={`auto-topup-threshold-${sim.iccid}`} type="number" min={thresholdType === "pct" ? 1 : 50} max={thresholdType === "pct" ? 90 : 102400}
                  value={thresholdValue} onChange={(e) => setThresholdValue(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm outline-none" />
                <select data-testid={`auto-topup-threshold-type-${sim.iccid}`} value={thresholdType} onChange={(e) => { setThresholdType(e.target.value); setThresholdValue(e.target.value === "pct" ? 10 : 500); }}
                  className="border-l border-slate-200 px-2 text-xs bg-white outline-none">
                  <option value="pct">%</option>
                  <option value="mb">MB</option>
                </select>
              </div>
            </label>
            <label className="block">
              <span className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("auto.packageMode")}</span>
              <select data-testid={`auto-topup-mode-${sim.iccid}`} value={mode} onChange={(e) => setMode(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35]">
                <option value="same">{t("auto.modeSame")}</option>
                <option value="custom">{t("auto.modeCustom")}</option>
                <option value="cheapest">{t("auto.modeCheapest")}</option>
              </select>
            </label>
            <label className="block">
              <span className="font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">{t("auto.endDate")}</span>
              <input data-testid={`auto-topup-end-date-${sim.iccid}`} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#FF6B35]" />
            </label>
            <div className="flex items-end">
              <button type="button" data-testid={`auto-topup-save-${sim.iccid}`} onClick={() => save(true)} disabled={busy || (mode === "custom" && !packageId)}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B35] text-white px-4 py-2.5 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} {t("admin.save")}
              </button>
            </div>
          </div>

          {mode === "custom" && (
            <select data-testid={`auto-topup-package-${sim.iccid}`} value={packageId} onChange={(e) => setPackageId(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-[#FF6B35]">
              <option value="">{t("auto.choosePackage")}</option>
              {(topups || []).map((p) => (
                <option key={p.package_id} value={p.package_id}>{p.data} · {fmt(p.price)}</option>
              ))}
            </select>
          )}
          <p className="text-xs text-slate-500">{t("auto.note")}</p>

          {history && history.length > 0 && (
            <div data-testid={`auto-topup-history-${sim.iccid}`} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400">
                {t("auto.historyTitle")}
              </div>
              <div className="divide-y divide-slate-100">
                {history.slice(0, 5).map((h) => (
                  <div key={h.run_key} data-testid={`auto-topup-history-row-${h.run_key}`} className="px-4 py-3 grid grid-cols-[1fr_auto] gap-3 text-xs">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 truncate">{h.package_title || h.package_id || "Top-up"}</div>
                      <div className="mt-1 text-slate-400">
                        {new Date(h.created_at).toLocaleString(lang)} · {t(`auto.method.${h.method}`)}
                      </div>
                      {h.error && <div className="mt-1 text-rose-500 truncate">{h.error}</div>}
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-slate-900">{historyAmount(h)}</div>
                      <span className={`mt-1 inline-block rounded-full px-2 py-0.5 font-mono-t text-[8px] font-bold uppercase tracking-widest ${
                        h.status === "succeeded" ? "bg-emerald-50 text-emerald-700" : h.status === "failed" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                        {t(`auto.status.${h.status}`)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
